/**
 * A member's face, or the next best thing.
 *
 * Migration 0036 put two photographs in the schema — the family portrait
 * on the account, the individual on the person — and described the second
 * as "what a member of staff looks at to recognise someone walking
 * through the door". This is the first screen that renders either.
 *
 * It has to work before a club has uploaded a single one, because most
 * clubs will not have, and a grid of empty squares is worse than a grid
 * of numbers. So the fallback is initials on a tinted ground, and the
 * tint is derived from the name rather than random: the same member is
 * the same colour every service, which is most of what recognition is.
 * A missing photograph and a broken URL land in the same place, because
 * from the floor they are the same thing.
 */

import { useEffect, useState } from 'react';
import { resolvePhoto } from '../lib/media.ts';

/* Six tints from the product's own palette range, chosen to stay legible
   under dark ink and distinct from each other at the size of a thumbnail.
   Not the series colors: those mean something on a chart. */
const TINTS = [
  { bg: '#dfe7f2', ink: '#254468' },
  { bg: '#e6e2d6', ink: '#5c4d24' },
  { bg: '#dde9e2', ink: '#26523f' },
  { bg: '#efdfe0', ink: '#6b3038' },
  { bg: '#e2e0ee', ink: '#3d3568' },
  { bg: '#dfeaee', ink: '#255460' },
];

function initials(name: string): string {
  // Names arrive both ways — "Whitfield, Charles" from an account and
  // "Charles Whitfield" from a person — and a comma is the tell.
  const parts = name.includes(',')
    ? name.split(',').map((p) => p.trim()).reverse()
    : name.trim().split(/\s+/);

  const letters = parts
    .filter(Boolean)
    .map((p) => p[0]!)
    .filter((c) => /\p{L}/u.test(c));

  if (letters.length === 0) return '?';
  if (letters.length === 1) return letters[0]!.toUpperCase();
  return (letters[0]! + letters[letters.length - 1]!).toUpperCase();
}

function tintFor(name: string) {
  let h = 0;
  for (let i = 0; i < name.length; i++) h = (h * 31 + name.charCodeAt(i)) >>> 0;
  return TINTS[h % TINTS.length]!;
}

export function Face({
  name,
  photoUri,
  size = 46,
  dim,
}: {
  name: string | null | undefined;
  photoUri?: string | null;
  size?: number;
  /** For a seat that has already signed, or a check that is closed. */
  dim?: boolean;
}) {
  const [broken, setBroken] = useState(false);
  /**
   * The bytes, once they have arrived.
   *
   * `photo_uri` is a key rather than a URL, and the route that serves it
   * is authenticated — so the image cannot simply be pointed at. Fetched
   * through a shared cache, so a screen with the same family on it four
   * times makes one request. An absolute URL comes straight back, which
   * is the club-with-its-own-photo-service case.
   */
  const [src, setSrc] = useState<string | null>(null);

  useEffect(() => {
    let alive = true;
    setSrc(null);
    setBroken(false);
    if (!photoUri) return;
    void resolvePhoto(photoUri).then((url) => {
      if (alive) setSrc(url);
    });
    return () => {
      alive = false;
    };
  }, [photoUri]);

  const label = name?.trim() || 'Nobody named';
  const tint = tintFor(label);

  const common = {
    width: size,
    height: size,
    borderRadius: '50%',
    flex: `0 0 ${size}px`,
    opacity: dim ? 0.45 : 1,
  } as const;

  // Initials while it loads, not a blank square. A face that pops in a
  // moment late is fine; a hole in a row of faces reads as a member
  // whose record is broken.
  if (src && !broken) {
    return (
      <img
        src={src}
        alt={label}
        title={label}
        onError={() => setBroken(true)}
        style={{ ...common, objectFit: 'cover', background: tint.bg }}
      />
    );
  }

  return (
    <span
      // A photograph is decorative here — the name is always beside it —
      // but the initials tile is the only thing on a small tile that
      // identifies anybody, so it carries the name for a screen reader.
      role="img"
      aria-label={label}
      title={label}
      style={{
        ...common,
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: tint.bg,
        color: tint.ink,
        fontWeight: 700,
        fontSize: Math.round(size * 0.36),
        letterSpacing: '0.02em',
        userSelect: 'none',
      }}
    >
      {name?.trim() ? initials(label) : '—'}
    </span>
  );
}

/**
 * The people at one check, overlapped the way a table of four reads at a
 * glance. Capped, with a count for the rest: a party of twelve is a
 * number, not twelve thumbnails.
 */
export function FaceRow({
  faces,
  fallbackName,
  fallbackPhoto,
  size = 34,
  max = 4,
}: {
  faces: { person_id: string; name: string; photo_uri: string | null }[];
  fallbackName?: string | null;
  fallbackPhoto?: string | null;
  size?: number;
  max?: number;
}) {
  if (faces.length === 0) {
    return <Face name={fallbackName} photoUri={fallbackPhoto} size={size} />;
  }

  const shown = faces.slice(0, max);
  const rest = faces.length - shown.length;

  return (
    <span style={{ display: 'inline-flex', alignItems: 'center' }}>
      {shown.map((f, i) => (
        <span
          key={f.person_id}
          style={{
            marginLeft: i === 0 ? 0 : -size * 0.3,
            // The near face on top, so the overlap reads as depth rather
            // than as a stack of cards falling over.
            zIndex: shown.length - i,
            borderRadius: '50%',
            boxShadow: '0 0 0 2px var(--surface, #fff)',
            display: 'inline-flex',
          }}
        >
          <Face name={f.name} photoUri={f.photo_uri} size={size} />
        </span>
      ))}
      {rest > 0 && (
        <span className="muted small" style={{ marginLeft: 6 }}>
          +{rest}
        </span>
      )}
    </span>
  );
}
